/**
 * Deterministic primary-client-scope selection (SPEC invoice-identity-cascade, R2 AC9).
 *
 * The single source of truth for "which client scope is the primary one" on the
 * frontend. The rule is IDENTICAL across all sites — frontend auto-pick here, the
 * backend `primary_client_scope_id` (models/projects.py), the billing-queue
 * `client_scopes` ordering (models/billing_session.py), and the PDF contact
 * fallback: `is_primary DESC, created_at ASC`.
 *
 * Before R2 the invoice-create auto-pick read `clientScopes.find(is_primary) ||
 * clientScopes[0]`, where `clientScopes[0]` was whatever order
 * get_project_scopes returned (scope TYPE display_order, no is_primary/created_at
 * tiebreak) — so a multi-client project with NO flagged primary picked a
 * non-deterministic scope that disagreed with the backend. This helper sorts by
 * the canonical rule so the FE pick matches the backend's primary every time.
 */

/** Minimal shape a scope must have to be ordered. Both `is_primary` and
 * `created_at` are nullable/optional in the wire schema; nulls sort LAST among
 * primaries (i.e. treated as not-primary) and missing created_at sorts last.
 * Nullability matches the ProjectScope wire schema so a ProjectScope[] is a
 * structural match without casting. */
export interface OrderableClientScope {
  id: string;
  is_primary?: boolean | null;
  created_at?: string | null;
  is_client_type?: boolean | null;
  organization_id?: string | null;
}

/**
 * Order client scopes by the canonical rule: is_primary DESC, created_at ASC.
 * Returns a NEW array (does not mutate the input). Non-client scopes are NOT
 * filtered here — callers that only want client scopes should filter first.
 */
export function orderByPrimaryRule<T extends OrderableClientScope>(scopes: T[]): T[] {
  return [...scopes].sort((a, b) => {
    // is_primary DESC: a primary scope sorts before a non-primary.
    const aPrimary = a.is_primary === true ? 1 : 0;
    const bPrimary = b.is_primary === true ? 1 : 0;
    if (aPrimary !== bPrimary) return bPrimary - aPrimary;
    // created_at ASC: oldest first (stable deterministic tiebreak). Missing
    // created_at sorts last so a real timestamp always wins.
    const aTime = a.created_at ? Date.parse(a.created_at) : Number.POSITIVE_INFINITY;
    const bTime = b.created_at ? Date.parse(b.created_at) : Number.POSITIVE_INFINITY;
    if (aTime !== bTime) return aTime - bTime;
    // Final deterministic tiebreak on id so the result is total-ordered even
    // when created_at ties (mirrors the backend ... , id ASC).
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * Pick the primary client scope from a list of (already client-filtered) scopes,
 * using the canonical rule. Returns undefined for an empty list.
 */
export function pickPrimaryClientScope<T extends OrderableClientScope>(
  clientScopes: T[]
): T | undefined {
  return orderByPrimaryRule(clientScopes)[0];
}
